import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import {
  defaultFeatureEnabled,
  FeatureKey,
  UserFeatureState,
  UserRole,
} from '../lib/adminFeatures';

export type { FeatureKey, UserRole };

interface UserAccessPayload {
  user_id: string;
  role: UserRole;
  features: UserFeatureState[];
}

const ALL_FEATURE_KEYS: FeatureKey[] = [
  'transactions',
  'budgets',
  'assistant_ia',
  'ocr_scan',
];

function buildDefaultFeatures(role: UserRole): UserFeatureState[] {
  return ALL_FEATURE_KEYS.map((feature_key) => ({
    feature_key,
    enabled: defaultFeatureEnabled(role, feature_key),
    is_override: false,
    role_default: defaultFeatureEnabled(role, feature_key),
  }));
}

function featuresToMap(features: UserFeatureState[]): Record<FeatureKey, boolean> {
  const map = {} as Record<FeatureKey, boolean>;
  for (const key of ALL_FEATURE_KEYS) {
    map[key] = defaultFeatureEnabled('free', key);
  }
  for (const f of features) {
    map[f.feature_key] = f.enabled;
  }
  return map;
}

async function loadRoleFromProfile(userId: string): Promise<UserRole> {
  const { data, error } = await supabase
    .from('profiles')
    .select('role')
    .eq('id', userId)
    .single();
  if (error) console.warn('[useUserAccess] profiles.role:', error.message);
  return (data?.role as UserRole) ?? 'free';
}

async function fetchUserAccess(userId: string): Promise<{
  role: UserRole;
  features: UserFeatureState[];
}> {
  const { data, error } = await supabase.rpc('get_my_features');

  if (!error && data) {
    const payload = data as UserAccessPayload;
    const role = (payload.role as UserRole) ?? 'free';
    const features = Array.isArray(payload.features)
      ? payload.features
      : buildDefaultFeatures(role);
    return { role, features };
  }

  if (error) {
    console.warn('[useUserAccess] get_my_features fallback:', error.message);
  }

  const role = await loadRoleFromProfile(userId);
  return { role, features: buildDefaultFeatures(role) };
}

export function useUserAccess() {
  const { user } = useAuth();
  const [role, setRole] = useState<UserRole>('free');
  const [featureMap, setFeatureMap] = useState(() =>
    featuresToMap(buildDefaultFeatures('free')),
  );
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user?.id) {
      setRole('free');
      setFeatureMap(featuresToMap(buildDefaultFeatures('free')));
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const { role: loadedRole, features } = await fetchUserAccess(user.id);
      setRole(loadedRole);
      setFeatureMap(featuresToMap(features));
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const hasFeature = useCallback(
    (key: FeatureKey): boolean => {
      if (key in featureMap) return featureMap[key];
      return defaultFeatureEnabled(role, key);
    },
    [featureMap, role],
  );

  return useMemo(
    () => ({
      role,
      loading,
      refresh,
      hasFeature,
      isFree: role === 'free',
      isPremium: role === 'premium' || role === 'admin',
      isAdmin: role === 'admin',
    }),
    [role, loading, refresh, hasFeature],
  );
}
